/**
 * Tracking validation helpers.
 *
 * Responsibility:
 * - Provide client-side validation aligned with backend rules.
 * - Avoid avoidable 400 responses by validating and normalizing references before submit.
 */

/**
 * Sanitizes a tracking reference by:
 * - Trimming whitespace,
 * - Removing internal whitespace,
 * - Removing leading/trailing punctuation separators,
 * - Normalizing to uppercase for stable comparisons.
 *
 * @param {any} value Raw user input.
 * @returns {string} Sanitized reference in uppercase.
 */
export const sanitizeTrackingReference = (value) => {
    if (value == null) return '';
    let v = String(value).trim();
    v = v.replace(/\s+/g, '');
    v = v.replace(/^[-.,;:/\\]+|[-.,;:/\\]+$/g, '');
    return v.toUpperCase();
};

/**
 * Validates an ocean container number.
 * Format: 4 letters + 7 digits (e.g. TEMU1234567).
 *
 * @param {any} v Raw user input.
 * @returns {boolean} True when the value matches the expected container format.
 */
export const isValidContainerNumber = (v) => {
    const value = sanitizeTrackingReference(v);
    return /^[A-Z]{4}[0-9]{7}$/.test(value);
};

/**
 * Validates an AWB number.
 * Supported formats:
 * - NNNNNNNNNNN (11 digits)
 * - NNN-NNNNNNNN
 *
 * @param {any} v Raw user input.
 * @returns {boolean} True when the value matches the expected AWB format.
 */
export const isValidAwb = (v) => {
    const value = String(v ?? '').trim();
    return /^\d{3}(-)?\d{8}$/.test(value);
};

/**
 * Normalizes an AWB number into canonical form: NNN-NNNNNNNN.
 * Assumes the input already matches isValidAwb().
 *
 * @param {any} awb Raw AWB input.
 * @returns {string} Canonical AWB string.
 */
export const normalizeAwb = (awb) => {
    const trimmed = String(awb ?? '').trim();
    return trimmed.replace(/^(\d{3})-?(\d{8})$/, '$1-$2');
};

/**
 * Validates an ocean booking/BL-like reference.
 * Rules:
 * - Must not be a container or AWB.
 * - Length between 6 and 35.
 * - Allowed characters: A-Z, 0-9 and '-'.
 *
 * @param {any} v Raw user input.
 * @returns {boolean} True when the value matches booking/BL validation constraints.
 */
export const isValidBookingOrBlNumber = (v) => {
    const value = sanitizeTrackingReference(v);

    if (!value) return false;
    if (isValidContainerNumber(value) || isValidAwb(value)) return false;
    if (value.length < 6 || value.length > 35) return false;
    return /^[A-Z0-9-]+$/.test(value);
};

/**
 * Validates an input and returns a normalized representation and its detected kind.
 *
 * @param {any} raw Raw user input.
 * @returns {{ ok: boolean, normalized: string, kind: 'container'|'awb'|'booking'|'unknown' }}
 * The detected kind is used to enforce mode-specific constraints during Create.
 */
export const validateTrackingReference = (raw) => {
    const sanitized = sanitizeTrackingReference(raw);

    if (isValidContainerNumber(sanitized)) {
        return { ok: true, normalized: sanitized, kind: 'container' };
    }

    if (isValidAwb(sanitized)) {
        return { ok: true, normalized: normalizeAwb(sanitized), kind: 'awb' };
    }

    if (isValidBookingOrBlNumber(sanitized)) {
        return { ok: true, normalized: sanitized, kind: 'booking' };
    }

    return { ok: false, normalized: sanitized, kind: 'unknown' };
};